import React, { useEffect, useState } from "react";
import {
  Grid,
  Typography,
  Button,
  Avatar,
  makeStyles,
  Paper,
  Card,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Box,
} from "@material-ui/core";
import GetAppIcon from "@material-ui/icons/GetApp";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import EditIcon from "@material-ui/icons/Edit";
import AddIcon from "@material-ui/icons/PersonAdd";
import SearchIcon from "@material-ui/icons/Search";
import BlockIcon from "@material-ui/icons/Block";
import Skeleton from '@material-ui/lab/Skeleton';
import { connect } from 'react-redux';
import AddHeaderDescriptionWithHelperText from "../../components/HelperTextDescription";
import { appService } from "../../App/app.service";
import EditUserModal from "./EditUserModal";
import { styled } from "@mui/material/styles";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  card: {
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
    boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
  },
  headerRow: {
    marginBottom: theme.spacing(2),
  },
  newUserBtn: {
    backgroundColor: "#4472c4",
    color: "#fff",
    textTransform: "none",
    "&:hover": {
      backgroundColor: "#303f9f",
    },
  },
  statusActive: {
    backgroundColor: "#c8e6c9",
    color: "#1b5e20",
    padding: "3px 14px",
    borderRadius: "14px",
    display: "inline-block",
    lineHeight: 1.2,
  },
  statusInactive: {
    backgroundColor: "#ffcdd2",
    color: "#b71c1c",
    padding: "3px 14px",
    borderRadius: "14px",
    display: "inline-block",
    lineHeight: 1.2,
  },
  userCell: {
    display: "flex",
    alignItems: "center",
  },
  avatar: {
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    marginRight: theme.spacing(1.6),
  },
  searchField: {
    marginBottom: theme.spacing(2),
    backgroundColor: "#fff",
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
    },
  },
  gridItem: {
    width: "100%",
    padding: "1rem",
  },
  actionButton: {
    minWidth: "auto",
    padding: "4px 8px",
    margin: "0 2px",
  },
  deactivateButton: {
    color: "#d32f2f",
    "&:hover": {
      backgroundColor: "rgba(211, 47, 47, 0.04)",
    },
  },
  editButton: {
    color: "#1976d2",
    "&:hover": {
      backgroundColor: "rgba(25, 118, 210, 0.04)",
    },
  },
  actionsContainer: {
    display: "flex",
    justifyContent: "flex-start",
    gap: theme.spacing(0.5),
  },
  tableHeaderCell: {
    backgroundColor: "#4472c4",
    color: "#fff",
    fontWeight: "bold",
    textAlign: "center",
    borderRight: "none",
    fontSize: "1rem",
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    paddingTop: '18px',
    paddingBottom: '18px',
  },
  dateHeaderCell: {
    backgroundColor: "#4472c4",
    color: "#fff",
    fontWeight: "bold",
    textAlign: "right",
    borderRight: "none",
    fontSize: "1rem",
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    maxWidth: 150,
    minWidth: 80,
    paddingTop: '18px',
    paddingBottom: '18px',
  },
  dateCell: {
    textAlign: 'right',
    fontSize: "0.95rem",
    borderRight: "none",
    verticalAlign: "middle",
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    maxWidth: 120,
    minWidth: 80,
  },
  tableCell: {
    textAlign: "center",
    fontSize: "0.95rem",
    borderRight: "none",
    verticalAlign: "middle",
  },
  tableRow: {
    '&:hover': {
      backgroundColor: '#e3ecfa', // lighter version of #4472c4
    },
  },
}));

const CustomWidthTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    minWidth: "none",
    fontSize: "1rem",
    color: "black",
    background: "white",
    fontWeight:400,
    border: "0.5px solid lightgrey",
  },
});

const ActiveUserManagement = (props) => {
  const classes = useStyles();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [deactivateModalOpen, setDeactivateModalOpen] = useState(false);
  const [userToDeactivate, setUserToDeactivate] = useState(null);
  const [activateModalOpen, setActivateModalOpen] = useState(false);
  const [userToActivate, setUserToActivate] = useState(null);

  // Sorting state
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // Sorting handler
  const handleSort = (columnKey) => {
    setSortConfig((prev) => {
      if (prev.key === columnKey) {
        // Toggle direction
        return { key: columnKey, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key: columnKey, direction: 'asc' };
    });
  };

  useEffect(() => {
    props.getUsersData();
  }, []);

  useEffect(() => {
    if (props.usersData && props.usersData !== "") {
      const filtered = props.usersData.filter(user => 
        user.Full_Name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.Email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.Role_Type?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredUsers(filtered);
    }
  }, [props.usersData, searchTerm]);

  // Sort users before rendering
  const sortedUsers = React.useMemo(() => {
    if (!sortConfig.key) return filteredUsers;
    return [...filteredUsers].sort((a, b) => {
      let aValue = a[sortConfig.key] || '';
      let bValue = b[sortConfig.key] || '';
      // Special handling for date
      if (sortConfig.key === 'Active_Last_Updated') {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      } else {
        aValue = aValue.toString().toLowerCase();
        bValue = bValue.toString().toLowerCase();
      }
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredUsers, sortConfig]);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleEditClick = (user) => {
    setSelectedUser(user);
    setEditModalOpen(true);
  };

  const handleModalClose = () => {
    setEditModalOpen(false);
    setSelectedUser(null);
  };

  const handleDeactivateClick = (user) => {
    setUserToDeactivate(user);
    setDeactivateModalOpen(true);
  };

  const handleDeactivateModalClose = () => {
    setDeactivateModalOpen(false);
    setUserToDeactivate(null);
  };

  const handleConfirmDeactivate = async () => {
    if (userToDeactivate) {
      props.deactivateUser(userToDeactivate.UserID,userToDeactivate.Email)
      handleDeactivateModalClose();
    }
  };

  const handleActivateClick = (user) => {
    setUserToActivate(user);
    setActivateModalOpen(true);
  };

  const handleActivateModalClose = () => {
    setActivateModalOpen(false);
    setUserToActivate(null);
  };

  const handleConfirmActivate = async () => {
    console.log("userToActivate",userToActivate)
    if (userToActivate) {
      props.activateUser(userToActivate.UserID,userToActivate.Email);
      handleActivateModalClose();
    }
  };

  const handleUpdateUser = (updatedUser) => {
    // Optionally, update local state if needed
    // For now, just close the modal
    setEditModalOpen(false);
    setSelectedUser(null);
    // Optionally, refresh user data if backend update is performed in EditUserModal
    props.getUsersData();
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const options = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'UTC'
    };
    return date.toLocaleString('en-US', options) + ' UTC';
  };

  const capitalize = (str) => str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : "-";

  const handleExportCSV = () => {
    const csvColumns = [
      { header: 'User', field: 'Full_Name' },
      { header: 'Email', field: 'Email' },
      { header: 'Role', field: 'Role_Type' },
      { header: 'Team', field: 'Display_Name' },
      { header: 'Status', field: 'IsActive' },
      { header: 'Last Status Change Date & Time (UTC)', field: 'Active_Last_Updated', formatter: formatDateTime },
    ];

    const headers = csvColumns.map(col => col.header).join(',');
    const csvRows = sortedUsers.map(row => {
      return csvColumns.map(col => {
        let value = col.formatter ? col.formatter(row[col.field]) : row[col.field];
        if (typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))) {
          value = `"${value.replace(/"/g, '""')}"`;
        }
        return value ?? '';
      }).join(',');
    });

    const csvContent = [headers, ...csvRows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'user_management_data.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Grid container justify="flex-start">
      <Grid item xs={12}>
        {props.usersData !== "" ? (
          <Grid style={{ padding: "0.5rem 0.5rem" }}>
            <Paper className={classes.card}>
              <Grid
                container
                justify="flex-start"
                alignItems="center"
                style={{ marginBottom: 16 }}
              >
                <Typography style={{fontWeight:600}} variant="h2">SkyGeni Users Currently Provisioned</Typography>
                <CustomWidthTooltip
                  title={<span>
                    This table displays all users currently provisioned in SkyGeni. You can search, sort, filter, and manage user status (activate/deactivate) using the controls above. Click "Provision User" to provision a existing CRM user in SkyGeni.
                  </span>}
                  arrow
                  placement="right"
                >
                  <IconButton  aria-label="help" size="medium">
              <HelpOutlineIcon fontSize="inherit" />
            </IconButton>
                </CustomWidthTooltip>
              </Grid>
              <Grid
                container
                justifyContent="space-between"
                alignItems="center"
                className={classes.headerRow}
              >
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Search users by name, email, or role..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    className={classes.searchField}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={4} container justifyContent="flex-end" alignItems="flex-end" direction="column" spacing={1}>
                  <Grid item>
                    <Button
                      onClick={() => props.setView("provisionUsersManagement")}
                      variant="contained"
                      color="primary"
                      startIcon={<AddIcon style={{ fontSize: 22 }} />}
                    >
                      Provision User
                    </Button>
                  </Grid>
                  <Grid item>
                    <Tooltip title="Download as CSV" arrow placement="left">
                      <IconButton
                        onClick={handleExportCSV}
                        style={{ padding: '8px', margin: '0 8px' }}
                      >
                        <GetAppIcon />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                </Grid>
              </Grid>
              <TableContainer component={Paper} style={{ maxHeight: '65vh', minHeight: '65vh', overflowY: 'auto' }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell className={classes.tableHeaderCell} onClick={() => handleSort('Full_Name')} style={{cursor:'pointer', textAlign:'left'}}>
                        User {sortConfig.key === 'Full_Name' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                      </TableCell>
                      <TableCell className={classes.tableHeaderCell} onClick={() => handleSort('Email')} style={{cursor:'pointer', textAlign:'left'}}>
                        Email {sortConfig.key === 'Email' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                      </TableCell>
                      <TableCell className={classes.tableHeaderCell} onClick={() => handleSort('Role_Type')} style={{cursor:'pointer', textAlign:'left'}}>
                        Role {sortConfig.key === 'Role_Type' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                      </TableCell>
                      <TableCell className={classes.tableHeaderCell} onClick={() => handleSort('Display_Name')} style={{cursor:'pointer', textAlign:'left'}}>
                        Team {sortConfig.key === 'Display_Name' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                      </TableCell>
                      <TableCell className={classes.tableHeaderCell} onClick={() => handleSort('IsActive')} style={{cursor:'pointer', textAlign:'left'}}>
                        Status {sortConfig.key === 'IsActive' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                      </TableCell>
                      <TableCell className={classes.dateHeaderCell} onClick={() => handleSort('Active_Last_Updated')} style={{cursor:'pointer'}}>
                        <span>Last Status Change<br /><span >Date & Time (UTC)</span></span>
                        {sortConfig.key === 'Active_Last_Updated' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                      </TableCell>
                      <TableCell className={classes.tableHeaderCell} style={{textAlign:'left'}}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedUsers.map((row) => (
                      <TableRow key={row.UserID} className={classes.tableRow}>
                        <TableCell className={classes.tableCell} style={{textAlign:'left'}}>
                          <div className={classes.userCell}>
                            <Avatar className={classes.avatar}>
                              {row.Full_Name.split(" ").map((n) => n[0]).join("").toUpperCase()}
                            </Avatar>
                            <span style={{ whiteSpace: 'pre' }}>{row.Full_Name}</span>
                          </div>
                        </TableCell>
                        <TableCell className={classes.tableCell} style={{textAlign:'left'}}>{row.Email}</TableCell>
                        <TableCell className={classes.tableCell} style={{textAlign:'left'}}>
                          <span style={{ textTransform: 'capitalize' }}>{capitalize(row.Role_Type)}</span>
                        </TableCell>
                        <TableCell className={classes.tableCell} style={{textAlign:'left'}}>{row.Display_Name}</TableCell>
                        <TableCell className={classes.tableCell} style={{textAlign:'left'}}>
                          {row.IsActive === "Active" ? (
                            <span className={classes.statusActive} style={{fontWeight:600}}>Active</span>
                          ) : (
                            <span className={classes.statusInactive} style={{fontWeight:600}}>Inactive</span>
                          )}
                        </TableCell>
                        <TableCell className={classes.dateCell}>
                          {formatDateTime(row.Active_Last_Updated)}
                        </TableCell>
                        <TableCell className={classes.tableCell} style={{textAlign:'left'}}>
                          {row.IsActive === "Active" && (
                            <CustomWidthTooltip 
                              title="Deactivate User" 
                              arrow
                              placement="right"
                              classes={{
                                tooltip: {
                                  fontSize: '0.7rem',
                                  padding: '8px 12px',
                                  maxWidth: '200px'
                                }
                              }}
                            >
                              <Button
                                className={`${classes.actionButton} ${classes.deactivateButton}`}
                                onClick={() => handleDeactivateClick(row)}
                              >
                                <BlockIcon fontSize="small" />
                              </Button>
                            </CustomWidthTooltip>
                          )}
                          {row.IsActive === "Inactive" && (
                            <CustomWidthTooltip 
                              title="Activate User" 
                              arrow
                              placement="right"
                              classes={{
                                tooltip: {
                                  fontSize: '0.7rem',
                                  padding: '8px 12px',
                                  maxWidth: '200px'
                                }
                              }}
                            >
                              <Button
                                className={`${classes.actionButton} ${classes.editButton}`}
                                onClick={() => handleActivateClick(row)}
                              >
                                <AddIcon fontSize="small" />
                              </Button>
                            </CustomWidthTooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        ) : (
          <Grid
            component={Card}
            item
            container
            style={{ margin: "1rem" }}
            justify="center"
          >
            <Skeleton
              variant="rect"
              animation="wave"
              height={120}
              width="90%"
              style={{ margin: "1rem" }}
            />
          </Grid>
        )}
      </Grid>

      <EditUserModal
        open={editModalOpen}
        onClose={handleModalClose}
        selectedUser={selectedUser}
        onUpdateUser={handleUpdateUser}
      />

      <Dialog
        open={deactivateModalOpen}
        onClose={handleDeactivateModalClose}
        aria-labelledby="deactivate-dialog-title"
        aria-describedby="deactivate-dialog-description"
      >
        <DialogTitle id="deactivate-dialog-title">
          Deactivate User
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="deactivate-dialog-description">
            <strong>{userToDeactivate?.Full_Name}</strong> will be deactivated and lose access to{" "}
            <span style={{ fontWeight: "bold" }}>SkyGeni</span>. 
            Are you sure you want to proceed?
          </DialogContentText>
        </DialogContent>
        <DialogActions style={{ justifyContent: 'center',paddingBottom:'1.5rem' }}>
        
          <Button 
            onClick={handleConfirmDeactivate} 
            color="primary" 
            variant="contained"
            autoFocus
          >
            Yes
          </Button>
            <Button color="primary" 
            variant="contained" onClick={handleDeactivateModalClose}>
            No
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={activateModalOpen}
        onClose={handleActivateModalClose}
        aria-labelledby="activate-dialog-title"
        aria-describedby="activate-dialog-description"
      >
        <DialogTitle id="activate-dialog-title">
          Activate User
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="activate-dialog-description">
            <strong>{userToActivate?.Full_Name}</strong> will be activated and gain access to{" "}
            <span style={{ fontWeight: "bold" }}>SkyGeni</span>.
            Are you sure you want to proceed?
          </DialogContentText>
        </DialogContent>
        <DialogActions style={{ justifyContent: 'center',paddingBottom:'1.5rem' }}>
          <Button 
            onClick={handleConfirmActivate} 
            color="primary" 
            variant="contained"
            autoFocus
          >
            Yes
          </Button>
          <Button onClick={handleActivateModalClose} variant="contained" color="primary">
            No
          </Button>
          
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

const mapStateToProps = (state) => {
  const { userMagementData, user } = state.app;

  return {
    usersData: userMagementData,
    user,
    Value_Label: user.Value_Label,
  };
};

const mapDispatchToProps = (dispatch) => ({
   deactivateUser: (userId,email) => {
    dispatch({ type: "deactivate_request" });
    appService.deactivateUser(userId,email).then(
      (json) => {
        dispatch({ type: "deactivate_success", json });
      },
      (error) => {
        if (typeof error === "object")
          dispatch({
            type: "deactivate_failure",
            error: "Something went wrong",
          });
        else dispatch({ type: "deactivate_failure", error });
      }
    );
  },
  activateUser: (userId,email) => {
    dispatch({ type: "activate_request" });
    appService.activateUser(userId,email).then(
      (json) => {
        dispatch({ type: "activate_success", json });
      },
      (error) => {
        if (typeof error === "object")
          dispatch({
            type: "activate_failure",
            error: "Something went wrong",
          });
        else dispatch({ type: "activate_failure", error });
      }
    );
  },
  getUsersData: (company) => {
    dispatch({ type: "get_users_data_request" });
    appService.getUsersData().then(
      (json) => {
        const transformedData = json.message.data.map((item, index) => ({
          UserID: item.UserID || index + 1,
          Active_Last_Updated:item.Active_Last_Updated||'',
          Full_Name: item.Full_Name || '-',
          Email: item.Email || '-',
          Role_Type: item.Role_Type || '-',
          Display_Name: item.Display_Name  || '-',
          IsActive: (item.IsActive ? 'Active' : 'Inactive') || '-',
        }));
        
        dispatch({ type: "get_users_data_success", json: transformedData });
      },
      (error) => {
        if (typeof error === "object")
          dispatch({
            type: "get_users_data_failure",
            error: "Something went wrong",
          });
        else dispatch({ type: "get_users_data_failure", error });
      }
    );
  },
});

const UserManagementSection = connect(
  mapStateToProps,
  mapDispatchToProps
)(ActiveUserManagement);

export default UserManagementSection;